import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Text,
    VStack,
    Box,
    Flex,
    Icon,
    HStack,
    Spinner,
    Center,
} from '@chakra-ui/react';
import React from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { TbCopy } from 'react-icons/tb';
import { ReactComponent as MotorIcon } from '../assets/Motor.svg';
import useGetPartNumberChange from '../hooks/useGetPartNumberChange';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import CopyTooltip from '../components/CopyTooltip';

interface PartNumberChangeModalProps {
    isOpen: boolean;
    onClose: () => void;
    vin: string;
    partNumber: string;
    correctedPartNumber: string;
}

const PartNumberChangeModal = ({ isOpen, onClose, partNumber, correctedPartNumber, vin }: PartNumberChangeModalProps) => {
    const { data, isLoading } = useGetPartNumberChange(partNumber, correctedPartNumber, vin, isOpen);
    const { copy, isCopied } = useCopyToClipboard();

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="lg" fontWeight="semibold" color="gray.800">Perfect Part Number</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    {isLoading ? (
                        <Center py={8}>
                            <VStack spacing={4}>
                                <Spinner size="lg" color="blue.500" />
                                <Text fontSize="sm" color="gray.600">Loading part information...</Text>
                            </VStack>
                        </Center>
                    ) : (
                        <VStack spacing={6} align="stretch">
                            <Text fontSize="md" color="gray.600">
                                An adjustment was made from the part number that was originally quoted
                            </Text>

                            <Box bg="gray.50" borderRadius="lg" p={6}>
                                <Flex align="center" justify="center" gap={6}>
                                    <Box flex={1} maxW="280px" bg="white" borderRadius="md" p={6} boxShadow="none" textAlign="center">
                                        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={3}>
                                            ORIGINAL
                                        </Text>
                                        <HStack spacing={2} mb={2} justify="center">
                                            <CopyTooltip isCopied={isCopied('original')}>
                                                <Text fontSize="xl" fontWeight="bold" fontFamily="mono">
                                                    {partNumber}
                                                </Text>
                                            </CopyTooltip>
                                            <Icon
                                                as={TbCopy as React.ElementType}
                                                w={5}
                                                h={5}
                                                color="gray.400"
                                                cursor="pointer"
                                                _hover={{ color: 'gray.600' }}
                                                onClick={() => copy(partNumber, 'original')}
                                            />
                                        </HStack>
                                        {data?.originalPart.partName && (
                                            <Text fontSize="sm" color="gray.600" mb={3}>
                                                {data.originalPart.partName}
                                            </Text>
                                        )}
                                        {data?.originalPart.qualifiers && data.originalPart.qualifiers.length > 0 && (
                                            <Text fontSize="xs" color="gray.500">
                                                {data.originalPart.qualifiers.join(', ')}
                                            </Text>
                                        )}
                                    </Box>

                                    <Icon as={FiArrowRight as React.ElementType} w={8} h={8} color="blue.500" flexShrink={0} />
                                    <Box flex={1} maxW="280px" bg="white" borderRadius="md" p={6} boxShadow="none" textAlign="center">
                                        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={3}>
                                            NEW
                                        </Text>
                                        <HStack spacing={2} mb={2} justify="center">
                                            <CopyTooltip isCopied={isCopied('corrected')}>
                                                <Text fontSize="xl" fontWeight="bold" fontFamily="mono">
                                                    {correctedPartNumber}
                                                </Text>
                                            </CopyTooltip>
                                            <Icon
                                                as={TbCopy as React.ElementType}
                                                w={5}
                                                h={5}
                                                color="gray.400"
                                                cursor="pointer"
                                                _hover={{ color: 'gray.600' }}
                                                onClick={() => copy(correctedPartNumber, 'corrected')}
                                            />
                                        </HStack>
                                        {data?.changedPart.partName && (
                                            <Text fontSize="sm" color="gray.600" mb={3}>
                                                {data.changedPart.partName}
                                            </Text>
                                        )}
                                        {data?.changedPart.qualifiers && data.changedPart.qualifiers.length > 0 && (
                                            <Text fontSize="xs" color="gray.500">
                                                {data.changedPart.qualifiers.join(', ')}
                                            </Text>
                                        )}
                                    </Box>
                                </Flex>
                            </Box>
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Flex justifyContent="center" alignItems="center" w="100%">
                        <HStack spacing={1} alignItems='center'>
                            <Text fontSize="xs" color="gray.600">
                                Part data powered by
                            </Text>
                            <MotorIcon style={{ width: '36px', height: '36px' }} />
                        </HStack>
                    </Flex>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default PartNumberChangeModal;
